import { useState, useMemo, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Post } from '../store/posts';
import { MediaFile, useMediaStore } from '../store/media';
import { MediaPicker } from './MediaPicker';
import { MediaUpload } from './MediaUpload';
import { PostPreview } from './PostPreview';
import { PostType, FORMAT_SPECS, Platform } from '../config/formatSpecs';
import { useHashtagsStore } from '../store/hashtags';
import { POST_STATUS, PostStatus as PostStatusType } from '../config/statusConfig';

// Type for ordered media items (can be from upload or library)
interface OrderedMediaItem {
  id: string;
  source: 'upload' | 'library';
  media: MediaFile;
}

// Helper function to format Date to local datetime-local input value
function formatToLocalDateTimeInput(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

// Post-Type-Auswahl mit Meta-Namen und Plattform-Kompatibilität
const POST_TYPE_OPTIONS: {
  id: PostType;
  icon: string;
  platforms: Platform[];
}[] = [
  { id: 'feed', icon: '📄', platforms: ['facebook', 'instagram'] },
  { id: 'reel', icon: '🎬', platforms: ['facebook', 'instagram'] },
  { id: 'story', icon: '📲', platforms: ['facebook', 'instagram'] },
  { id: 'carousel', icon: '🎠', platforms: ['facebook', 'instagram'] },
];

function getMetaFormatName(platform: Platform, postType: PostType, hasVideo: boolean): string {
  const spec = FORMAT_SPECS.find(
    (s) =>
      s.platform === platform &&
      s.postType === postType &&
      (postType === 'reel' || postType === 'story' || s.mediaType === (hasVideo ? 'video' : 'image') || s.mediaType === 'both')
  );
  return spec?.nameDe || `${platform} ${postType}`;
}

interface DeleteResult {
  success: boolean;
  requiresPlatformDelete?: boolean;
  publishedIds?: Record<string, string>;
  platforms?: string[];
  platformResults?: { platform: string; success: boolean; error?: string }[];
  error?: string;
}

interface PostDetailModalProps {
  post: Post | null;
  isOpen: boolean;
  onClose: () => void;
  onUpdate: (postId: string, data: { text?: string; scheduled_for?: string; platforms?: string[]; status?: 'draft' | 'scheduled'; media_urls?: string[]; post_type?: PostType; flight_id?: string | null; hashtags?: string[] }) => Promise<void>;
  onDelete: (postId: string, options?: { deleteFromPlatforms?: boolean }) => Promise<DeleteResult>;
  onCancel: (postId: string) => Promise<void>;
  onDuplicate?: (postId: string) => Promise<void>;
  flights?: { id: string; name: string; color: string }[];
}

export function PostDetailModal({ post, isOpen, onClose, onUpdate, onDelete, onCancel, onDuplicate, flights = [] }: PostDetailModalProps) {
  const { t } = useTranslation(['posts', 'common']);
  const { media: libraryMedia, fetchMedia } = useMediaStore();
  const [isEditing, setIsEditing] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [text, setText] = useState('');
  const [platforms, setPlatforms] = useState<string[]>([]);
  const [scheduledFor, setScheduledFor] = useState('');
  const [status, setStatus] = useState<'draft' | 'scheduled'>('draft');
  const [selectedPostType, setSelectedPostType] = useState<PostType>('feed');
  const [uploadedMedia, setUploadedMedia] = useState<MediaFile[]>([]);
  const [libraryMediaIds, setLibraryMediaIds] = useState<string[]>([]);
  const [libraryMediaUrls, setLibraryMediaUrls] = useState<string[]>([]);
  const [orderedMedia, setOrderedMedia] = useState<OrderedMediaItem[]>([]);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedFlightId, setSelectedFlightId] = useState<string | null>(null);
  const [hashtags, setHashtags] = useState<string[]>([]);
  const [hashtagInput, setHashtagInput] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmData, setDeleteConfirmData] = useState<{
    requiresPlatformDelete: boolean;
    publishedIds?: Record<string, string>;
    platforms?: string[];
  } | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Lade Medien wenn Modal geöffnet wird
  useEffect(() => {
    if (isOpen && post) {
      fetchMedia(post.project_id);
    }
  }, [isOpen, post, fetchMedia]);

  // Reset beim Schließen
  useEffect(() => {
    if (!isOpen) {
      setIsEditing(false);
      setShowPreview(false);
      setUploadedMedia([]);
      setLibraryMediaIds([]);
      setLibraryMediaUrls([]);
      setOrderedMedia([]);
      setDraggedIndex(null);
      setDragOverIndex(null);
      setShowDeleteConfirm(false);
      setDeleteConfirmData(null);
      setDeleteError(null);
    }
  }, [isOpen]);

  // Kombiniere hochgeladene und Bibliotheks-Medien für die Vorschau
  // Use orderedMedia if available (maintains user's custom order)
  const allMediaForPreview = useMemo(() => {
    if (orderedMedia.length > 0) {
      return orderedMedia.map(item => item.media);
    }
    const librarySelected = libraryMedia.filter((m) => libraryMediaIds.includes(m.id));
    return [...uploadedMedia, ...librarySelected];
  }, [orderedMedia, uploadedMedia, libraryMedia, libraryMediaIds]);

  // Gesamtzahl der ausgewählten Medien (für Bedingungen)
  const totalMediaCount = uploadedMedia.length + libraryMediaIds.length;

  // Items für die Reihenfolge-Anzeige (verwendet orderedMedia wenn verfügbar, sonst baut es aus allMediaForPreview)
  const displayOrderItems = useMemo((): OrderedMediaItem[] => {
    if (orderedMedia.length > 0) {
      return orderedMedia;
    }
    // Fallback: Baue Items aus allMediaForPreview
    return allMediaForPreview.map((m) => ({
      id: m.id,
      source: libraryMediaIds.includes(m.id) ? 'library' as const : 'upload' as const,
      media: m
    }));
  }, [orderedMedia, allMediaForPreview, libraryMediaIds]);

  // Sync orderedMedia when uploadedMedia or libraryMediaIds change
  useEffect(() => {
    if (!isEditing) return;

    setOrderedMedia(prevOrderedMedia => {
      // Build the new ordered list, preserving existing order where possible
      const newOrderedMedia: OrderedMediaItem[] = [];

      // Keep existing items that are still valid
      for (const item of prevOrderedMedia) {
        if (item.source === 'upload') {
          const stillExists = uploadedMedia.some(m => m.id === item.id);
          if (stillExists) {
            const media = uploadedMedia.find(m => m.id === item.id);
            if (media) {
              newOrderedMedia.push({ ...item, media });
            }
          }
        } else {
          const stillSelected = libraryMediaIds.includes(item.id);
          if (stillSelected) {
            const media = libraryMedia.find(m => m.id === item.id);
            if (media) {
              newOrderedMedia.push({ ...item, media });
            }
          }
        }
      }

      // Add new uploaded items at the end
      for (const media of uploadedMedia) {
        const alreadyInList = newOrderedMedia.some(item => item.id === media.id && item.source === 'upload');
        if (!alreadyInList) {
          newOrderedMedia.push({ id: media.id, source: 'upload', media });
        }
      }

      // Add new library items at the end
      for (const mediaId of libraryMediaIds) {
        const alreadyInList = newOrderedMedia.some(item => item.id === mediaId && item.source === 'library');
        if (!alreadyInList) {
          const media = libraryMedia.find(m => m.id === mediaId);
          if (media) {
            newOrderedMedia.push({ id: mediaId, source: 'library', media });
          }
        }
      }

      // Only update if there's a real change
      if (JSON.stringify(newOrderedMedia.map(m => m.id)) !== JSON.stringify(prevOrderedMedia.map(m => m.id))) {
        return newOrderedMedia;
      }
      return prevOrderedMedia;
    });
  }, [uploadedMedia, libraryMediaIds, libraryMedia, isEditing]);

  // Drag and drop handlers
  const handleDragStart = useCallback((index: number) => {
    setDraggedIndex(index);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex !== null && draggedIndex !== index) {
      setDragOverIndex(index);
    }
  }, [draggedIndex]);

  const handleDragEnd = useCallback(() => {
    if (draggedIndex !== null && dragOverIndex !== null && draggedIndex !== dragOverIndex) {
      setOrderedMedia(prev => {
        const newOrder = [...prev];
        const [draggedItem] = newOrder.splice(draggedIndex, 1);
        newOrder.splice(dragOverIndex, 0, draggedItem);
        return newOrder;
      });
    }
    setDraggedIndex(null);
    setDragOverIndex(null);
  }, [draggedIndex, dragOverIndex]);

  const handleRemoveMedia = useCallback((index: number) => {
    // Verwende displayOrderItems als Fallback wenn orderedMedia noch leer ist
    const items = orderedMedia.length > 0 ? orderedMedia : allMediaForPreview.map((m) => ({
      id: m.id,
      source: libraryMediaIds.includes(m.id) ? 'library' as const : 'upload' as const,
      media: m
    }));
    const item = items[index];
    if (!item) return;

    if (item.source === 'upload') {
      setUploadedMedia(prev => prev.filter(m => m.id !== item.id));
    } else {
      setLibraryMediaIds(prev => prev.filter(id => id !== item.id));
      setLibraryMediaUrls(prev => prev.filter(url => url !== item.media.url));
    }
  }, [orderedMedia, allMediaForPreview, libraryMediaIds]);

  if (!isOpen || !post) return null;

  const canEdit = ['draft', 'scheduled'].includes(post.status);
  const canDelete = ['draft', 'scheduled', 'failed', 'cancelled', 'published'].includes(post.status);
  const canCancel = post.status === 'scheduled';
  const canReactivate = post.status === 'cancelled'; // Failed posts cannot be reactivated - must be duplicated instead
  const canDuplicate = true; // All posts can be duplicated
  const isPublished = post.status === 'published';

  const handleEdit = () => {
    setText(post.text);
    setPlatforms([...post.platforms]);
    setScheduledFor(formatToLocalDateTimeInput(new Date(post.scheduled_for)));
    setStatus(post.status === 'scheduled' ? 'scheduled' : 'draft');
    setSelectedPostType(post.post_type || 'feed');
    setUploadedMedia([]);
    setSelectedFlightId(post.flight_id || null);
    setHashtags(post.hashtags || []);
    setHashtagInput('');

    // Finde existierende Medien in der Bibliothek basierend auf URLs
    // WICHTIG: Behalte die Reihenfolge aus media_urls bei!
    const existingUrls = post.media_urls || [];
    const matchedMedia: MediaFile[] = [];
    for (const url of existingUrls) {
      const found = libraryMedia.find((m) => m.url === url);
      if (found) {
        matchedMedia.push(found);
      }
    }
    setLibraryMediaIds(matchedMedia.map((m) => m.id));
    setLibraryMediaUrls(matchedMedia.map((m) => m.url));

    // Initialisiere orderedMedia mit der richtigen Reihenfolge aus media_urls
    const initialOrderedMedia: OrderedMediaItem[] = matchedMedia.map((m) => ({
      id: m.id,
      source: 'library' as const,
      media: m
    }));
    setOrderedMedia(initialOrderedMedia);

    setIsEditing(true);
  };

  const handleLibraryMediaChange = (mediaIds: string[], urls: string[]) => {
    setLibraryMediaIds(mediaIds);
    setLibraryMediaUrls(urls);
  };

  const handleSave = async () => {
    setIsSubmitting(true);
    try {
      // Kombiniere alle Medien-URLs in der Reihenfolge des orderedMedia Arrays
      const allMediaUrls = orderedMedia.length > 0
        ? orderedMedia.map((item) => item.media.url).filter((url) => url && url.trim() !== '')
        : [
            ...uploadedMedia.map((m) => m.url),
            ...libraryMediaUrls,
          ].filter((url) => url && url.trim() !== '');

      await onUpdate(post.id, {
        text,
        platforms,
        post_type: selectedPostType,
        scheduled_for: new Date(scheduledFor).toISOString(),
        status,
        media_urls: allMediaUrls,
        flight_id: selectedFlightId,
        hashtags,
      });
      setIsEditing(false);
      setShowPreview(false);
      onClose();
    } catch {
      // Error handled in store
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (confirmPlatformDelete = false) => {
    setDeleteError(null);
    setIsSubmitting(true);
    try {
      const result = await onDelete(post.id, { deleteFromPlatforms: confirmPlatformDelete });

      if (result.requiresPlatformDelete) {
        // Show platform delete confirmation dialog
        setDeleteConfirmData({
          requiresPlatformDelete: true,
          publishedIds: result.publishedIds,
          platforms: result.platforms,
        });
        setShowDeleteConfirm(true);
        setIsSubmitting(false);
        return;
      }

      if (!result.success) {
        setDeleteError(result.error || t('common:error'));
        setIsSubmitting(false);
        return;
      }

      onClose();
    } catch (error) {
      setDeleteError(error instanceof Error ? error.message : t('common:error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteInitiate = () => {
    // For published posts, check if platform delete is needed
    if (post.status === 'published' && post.published_ids && Object.keys(post.published_ids).length > 0) {
      setDeleteConfirmData({
        requiresPlatformDelete: true,
        publishedIds: post.published_ids,
        platforms: post.platforms,
      });
      setShowDeleteConfirm(true);
    } else {
      // Simple confirmation for non-published posts
      if (confirm(t('posts:confirmDelete'))) {
        handleDelete(false);
      }
    }
  };

  const handleConfirmPlatformDelete = () => {
    setShowDeleteConfirm(false);
    handleDelete(true);
  };

  const handleCancelPost = async () => {
    if (!confirm(t('posts:confirmCancel'))) return;
    setIsSubmitting(true);
    try {
      await onCancel(post.id);
      onClose();
    } catch {
      // Error handled in store
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReactivate = async (newStatus: 'draft' | 'scheduled') => {
    setIsSubmitting(true);
    try {
      await onUpdate(post.id, { status: newStatus });
      onClose();
    } catch {
      // Error handled in store
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDuplicate = async () => {
    if (!onDuplicate) return;
    setIsSubmitting(true);
    try {
      await onDuplicate(post.id);
      onClose();
    } catch {
      // Error handled in store
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePlatformToggle = (platform: string) => {
    setPlatforms((prev) =>
      prev.includes(platform) ? prev.filter((p) => p !== platform) : [...prev, platform]
    );
  };

  const hasVideo = allMediaForPreview.some((m) => m.mime_type.startsWith('video/'));

  const currentStatusConfig = POST_STATUS[post.status as PostStatusType];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header with status accent */}
        <div className={`flex justify-between items-center p-4 border-b border-t-4 ${currentStatusConfig.accent}`}>
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold">
              {isEditing ? t('posts:editPost') : t('posts:detail.title')}
            </h2>
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 text-xs font-medium rounded-full ${currentStatusConfig.bg} ${currentStatusConfig.text}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${currentStatusConfig.dot}`} />
              {t(`posts:status.${post.status}`)}
            </span>
            {post.display_id && (
              <span className="text-xs text-gray-500">{post.display_id}</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {isEditing && (
              <button
                type="button"
                onClick={() => setShowPreview(!showPreview)}
                className={`px-3 py-1.5 text-sm rounded-md ${
                  showPreview ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
                }`}
              >
                {showPreview ? t('posts:detail.editor') : t('posts:detail.preview')}
              </button>
            )}
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {isEditing ? (
            showPreview ? (
              /* Preview Mode */
              <PostPreview
                text={text}
                platforms={platforms}
                media={allMediaForPreview}
                scheduledFor={scheduledFor}
                selectedPostType={selectedPostType}
              />
            ) : (
              /* Edit Mode */
              <>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('posts:text')}</label>
                  <textarea
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-sm text-gray-500 mt-1">{t('posts:detail.characters', { count: text.length })}</p>
                </div>

                {/* Hashtag Analysis for Instagram */}
                {platforms.includes('instagram') && (
                  <HashtagAnalysisSection text={text} />
                )}

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('posts:platforms')}</label>
                  <div className="flex gap-4">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={platforms.includes('facebook')}
                        onChange={() => handlePlatformToggle('facebook')}
                        className="mr-2 h-4 w-4 text-blue-600 rounded"
                      />
                      <span className="flex items-center">
                        <svg className="w-5 h-5 mr-1 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                        </svg>
                        Facebook
                      </span>
                    </label>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={platforms.includes('instagram')}
                        onChange={() => handlePlatformToggle('instagram')}
                        className="mr-2 h-4 w-4 text-pink-600 rounded"
                      />
                      <span className="flex items-center">
                        <svg className="w-5 h-5 mr-1 text-pink-600" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                        </svg>
                        Instagram
                      </span>
                    </label>
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('posts:scheduledFor')}</label>
                  <input
                    type="datetime-local"
                    value={scheduledFor}
                    onChange={(e) => setScheduledFor(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Post-Type Selection */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('posts:postType.label')}</label>
                  <div className="flex flex-wrap gap-2">
                    {POST_TYPE_OPTIONS.map((option) => {
                      const isCompatible = platforms.every((p) =>
                        option.platforms.includes(p as Platform)
                      );
                      const isSelected = selectedPostType === option.id;

                      return (
                        <button
                          key={option.id}
                          type="button"
                          onClick={() => isCompatible && setSelectedPostType(option.id)}
                          disabled={!isCompatible}
                          className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors ${
                            isSelected
                              ? 'border-blue-500 bg-blue-50 text-blue-700'
                              : isCompatible
                              ? 'border-gray-200 hover:border-gray-300 text-gray-700'
                              : 'border-gray-100 bg-gray-50 text-gray-400 cursor-not-allowed'
                          }`}
                        >
                          <span className="text-lg">{option.icon}</span>
                          <div className="text-left">
                            <div className="text-sm font-medium">{t(`posts:postType.${option.id}`)}</div>
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {/* Meta Format-Namen */}
                  {platforms.length > 0 && (
                    <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-500 mb-2">{t('posts:detail.metaApiFormats')}:</p>
                      <div className="flex flex-wrap gap-2">
                        {platforms.map((platform) => {
                          const formatName = getMetaFormatName(platform as Platform, selectedPostType, hasVideo);
                          return (
                            <span
                              key={platform}
                              className={`px-2 py-1 text-xs rounded-full ${
                                platform === 'facebook'
                                  ? 'bg-blue-100 text-blue-800'
                                  : 'bg-pink-100 text-pink-800'
                              }`}
                            >
                              {formatName}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                {/* Media Upload */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('posts:uploadMedia')}
                  </label>
                  <MediaUpload
                    projectId={post.project_id}
                    selectedMedia={uploadedMedia}
                    onMediaSelect={setUploadedMedia}
                    maxFiles={10}
                  />
                </div>

                {/* Media from Library */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('posts:mediaFromLibrary')}
                  </label>
                  <MediaPicker
                    projectId={post.project_id}
                    selectedMediaIds={libraryMediaIds}
                    onSelectionChange={handleLibraryMediaChange}
                    maxSelection={10 - uploadedMedia.length}
                  />
                  {platforms.includes('instagram') && allMediaForPreview.length === 0 && (
                    <p className="text-sm text-amber-600 mt-2">
                      {t('posts:instagram.requiresMedia')}
                    </p>
                  )}
                </div>

                {/* Reorderable Media Preview */}
                {totalMediaCount > 1 && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      {t('posts:mediaOrder')}
                      <span className="text-xs font-normal text-gray-400">({t('posts:dragToSort')})</span>
                    </label>
                    <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                      {displayOrderItems.map((item, index) => (
                        <div
                          key={`${item.source}-${item.id}`}
                          draggable
                          onDragStart={() => handleDragStart(index)}
                          onDragOver={(e) => handleDragOver(e, index)}
                          onDragEnd={handleDragEnd}
                          className={`relative group cursor-grab active:cursor-grabbing transition-all duration-150 ${
                            draggedIndex === index ? 'opacity-50 scale-95' : ''
                          } ${
                            dragOverIndex === index ? 'ring-2 ring-blue-500 ring-offset-2' : ''
                          }`}
                        >
                          {/* Position Badge */}
                          <div className="absolute -top-2 -left-2 w-5 h-5 bg-blue-600 text-white text-xs font-bold rounded-full flex items-center justify-center z-10">
                            {index + 1}
                          </div>

                          {/* Thumbnail */}
                          {item.media.mime_type.startsWith('video/') ? (
                            <div className="w-20 h-20 bg-gray-900 rounded-lg flex items-center justify-center overflow-hidden">
                              <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M8 5v14l11-7z" />
                              </svg>
                            </div>
                          ) : (
                            <img
                              src={item.media.url}
                              alt={`Media ${index + 1}`}
                              className="w-20 h-20 object-cover rounded-lg border border-gray-200"
                              draggable={false}
                            />
                          )}

                          {/* Source Badge */}
                          <div className={`absolute -bottom-1 left-1/2 -translate-x-1/2 px-1.5 py-0.5 text-[10px] font-medium rounded ${
                            item.source === 'upload' ? 'bg-green-100 text-green-700' : 'bg-purple-100 text-purple-700'
                          }`}>
                            {item.source === 'upload' ? t('posts:new') : t('posts:library')}
                          </div>

                          {/* Drag Handle Overlay */}
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 rounded-lg transition-all flex items-center justify-center">
                            <svg className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 drop-shadow-lg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                            </svg>
                          </div>

                          {/* Remove Button */}
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); handleRemoveMedia(index); }}
                            className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center hover:bg-red-600"
                            title={t('posts:remove')}
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {t('posts:mediaCount', { count: totalMediaCount })} - {t('posts:firstImageMain')}
                    </p>
                  </div>
                )}

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('common:status')}</label>
                  <div className="flex gap-4">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="status"
                        value="draft"
                        checked={status === 'draft'}
                        onChange={() => setStatus('draft')}
                        className="mr-2 h-4 w-4 text-gray-600"
                      />
                      <span className="flex items-center gap-1">
                        <span className="w-2 h-2 rounded-full bg-gray-400"></span>
                        {t('posts:status.draft')}
                      </span>
                    </label>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="status"
                        value="scheduled"
                        checked={status === 'scheduled'}
                        onChange={() => setStatus('scheduled')}
                        className="mr-2 h-4 w-4 text-blue-600"
                      />
                      <span className="flex items-center gap-1">
                        <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                        {t('posts:status.scheduled')}
                      </span>
                    </label>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {status === 'draft' ? t('posts:detail.statusDescription.draft') : t('posts:detail.statusDescription.scheduled')}
                  </p>
                </div>

                {/* Flight Assignment */}
                {flights.length > 0 && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('posts:flight')}</label>
                    <select
                      value={selectedFlightId || ''}
                      onChange={(e) => setSelectedFlightId(e.target.value || null)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">{t('posts:noFlight')}</option>
                      {flights.map((flight) => (
                        <option key={flight.id} value={flight.id}>
                          {flight.name}
                        </option>
                      ))}
                    </select>
                    {selectedFlightId && (
                      <div className="mt-2 flex items-center gap-2">
                        <span
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: flights.find(f => f.id === selectedFlightId)?.color || '#3B82F6' }}
                        />
                        <span className="text-sm text-gray-600">
                          {flights.find(f => f.id === selectedFlightId)?.name}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {/* Hashtags */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                    {t('posts:hashtags')}
                    <span className="text-xs font-normal text-gray-400">(Instagram)</span>
                  </label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {hashtags.map((tag, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-sm rounded-full"
                      >
                        #{tag}
                        <button
                          type="button"
                          onClick={() => setHashtags(hashtags.filter((_, i) => i !== index))}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={hashtagInput}
                      onChange={(e) => setHashtagInput(e.target.value.replace(/^#/, ''))}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && hashtagInput.trim()) {
                          e.preventDefault();
                          const newTag = hashtagInput.trim().replace(/^#/, '');
                          if (newTag && !hashtags.includes(newTag)) {
                            setHashtags([...hashtags, newTag]);
                          }
                          setHashtagInput('');
                        }
                      }}
                      placeholder={t('posts:hashtagPlaceholder')}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const newTag = hashtagInput.trim().replace(/^#/, '');
                        if (newTag && !hashtags.includes(newTag)) {
                          setHashtags([...hashtags, newTag]);
                        }
                        setHashtagInput('');
                      }}
                      disabled={!hashtagInput.trim()}
                      className="px-3 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      title={t('common:add')}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {t('posts:hashtagCount', { count: hashtags.length })}
                    {hashtags.length > 0 && ` - ${t('posts:hashtagAnalyzer')}`}
                  </p>
                </div>
              </>
            )
          ) : (
            <>
              {/* View Mode */}
              <div className="mb-4">
                <div className="flex gap-2 mb-3 flex-wrap">
                  {/* Post-Type Badge */}
                  {post.post_type && (
                    <span className={`inline-flex items-center px-2 py-1 text-xs rounded-full ${
                      post.post_type === 'feed' ? 'bg-gray-100 text-gray-700' :
                      post.post_type === 'reel' ? 'bg-purple-100 text-purple-700' :
                      post.post_type === 'story' ? 'bg-orange-100 text-orange-700' :
                      'bg-teal-100 text-teal-700'
                    }`}>
                      {post.post_type === 'feed' ? '📄' :
                       post.post_type === 'reel' ? '🎬' :
                       post.post_type === 'story' ? '📲' : '🎠'}
                      <span className="ml-1">
                        {t(`posts:postType.${post.post_type}`)}
                      </span>
                    </span>
                  )}
                  {post.platforms.map((platform) => (
                    <span
                      key={platform}
                      className={`inline-flex items-center px-2 py-1 text-xs rounded-full ${
                        platform === 'facebook' ? 'bg-blue-100 text-blue-800' : 'bg-pink-100 text-pink-800'
                      }`}
                    >
                      {platform === 'facebook' ? (
                        <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                        </svg>
                      ) : (
                        <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069z" />
                        </svg>
                      )}
                      {platform}
                    </span>
                  ))}
                </div>
                <p className="text-gray-800 whitespace-pre-wrap">{post.text}</p>
              </div>

              {/* Media Preview in View Mode */}
              {post.media_urls && post.media_urls.length > 0 && (
                <div className="mb-4">
                  <span className="text-sm font-medium text-gray-700 mb-2 block">{t('posts:media')} ({post.media_urls.length})</span>
                  <div className="flex flex-wrap gap-2">
                    {post.media_urls.map((url, index) => (
                      <div key={index} className="relative">
                        {url.match(/\.(mp4|mov|webm)$/i) ? (
                          <div className="w-20 h-20 bg-gray-900 rounded-lg flex items-center justify-center">
                            <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M8 5v14l11-7z" />
                            </svg>
                          </div>
                        ) : (
                          <img
                            src={url}
                            alt={`Media ${index + 1}`}
                            className="w-20 h-20 object-cover rounded-lg border border-gray-200"
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Flight Badge in View Mode */}
              {post.flight_id && flights.length > 0 && (
                <div className="mb-4">
                  <span className="text-sm font-medium text-gray-700 mb-2 block">{t('posts:flight')}</span>
                  {(() => {
                    const flight = flights.find(f => f.id === post.flight_id);
                    return flight ? (
                      <div className="flex items-center gap-2">
                        <span
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: flight.color || '#3B82F6' }}
                        />
                        <span className="text-sm text-gray-800">{flight.name}</span>
                      </div>
                    ) : null;
                  })()}
                </div>
              )}

              {/* Hashtags in View Mode */}
              {post.hashtags && post.hashtags.length > 0 && (
                <div className="mb-4">
                  <span className="text-sm font-medium text-gray-700 mb-2 block">{t('posts:hashtags')}</span>
                  <div className="flex flex-wrap gap-1.5">
                    {post.hashtags.map((tag, index) => (
                      <span
                        key={index}
                        className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">{t('posts:scheduledFor')}:</span>
                  <p className="font-medium">
                    {new Date(post.scheduled_for).toLocaleDateString(undefined, {
                      weekday: 'long',
                      day: '2-digit',
                      month: 'long',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </p>
                </div>
                <div>
                  <span className="text-gray-500">{t('posts:createdAt')}:</span>
                  <p className="font-medium">
                    {new Date(post.created_at).toLocaleDateString(undefined, {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                    })}
                  </p>
                </div>
              </div>

              {post.error_message && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm text-red-700">
                    <span className="font-medium">{t('posts:detail.error')}:</span> {post.error_message}
                  </p>
                </div>
              )}

              {post.published_at && (
                <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-md">
                  <p className="text-sm text-green-700">
                    <span className="font-medium">{t('posts:detail.publishedAt')}:</span>{' '}
                    {new Date(post.published_at).toLocaleDateString(undefined, {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer Actions */}
        <div className="flex justify-between items-center p-4 border-t bg-gray-50">
          {isEditing ? (
            <>
              <button
                onClick={() => {
                  setIsEditing(false);
                  setShowPreview(false);
                }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md"
                disabled={isSubmitting}
              >
                {t('common:cancel')}
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                disabled={isSubmitting || platforms.length === 0}
              >
                {isSubmitting ? t('posts:detail.saving') : t('posts:detail.save')}
              </button>
            </>
          ) : (
            <>
              <div className="flex gap-2">
                {canDelete && (
                  <button
                    onClick={handleDeleteInitiate}
                    className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-md"
                    disabled={isSubmitting}
                    title={isPublished ? t('posts:detail.deleteAlsoOnPlatforms') : t('posts:deletePost')}
                  >
                    {isPublished ? t('posts:detail.deleteFromPlatform') : t('common:delete')}
                  </button>
                )}
                {canCancel && (
                  <button
                    onClick={handleCancelPost}
                    className="px-4 py-2 text-orange-600 hover:bg-orange-50 rounded-md"
                    disabled={isSubmitting}
                  >
                    {t('posts:detail.cancelScheduled')}
                  </button>
                )}
              </div>
              <div className="flex gap-2">
                {canReactivate && (
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleReactivate('draft')}
                      className="px-3 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 text-sm"
                      disabled={isSubmitting}
                      title={t('posts:detail.reactivateAsDraft')}
                    >
                      {t('posts:detail.asDraft')}
                    </button>
                    <button
                      onClick={() => handleReactivate('scheduled')}
                      className="px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm"
                      disabled={isSubmitting}
                      title={t('posts:detail.reactivateScheduled')}
                    >
                      {t('posts:detail.reactivate')}
                    </button>
                  </div>
                )}
                {canDuplicate && onDuplicate && (
                  <button
                    onClick={handleDuplicate}
                    className="px-4 py-2 text-purple-600 border border-purple-300 rounded-md hover:bg-purple-50 flex items-center gap-1.5"
                    disabled={isSubmitting}
                    title={t('posts:detail.duplicateAsDraft')}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    {t('common:duplicate')}
                  </button>
                )}
                {canEdit && (
                  <button
                    onClick={handleEdit}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    {t('posts:detail.edit')}
                  </button>
                )}
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  {t('posts:detail.close')}
                </button>
              </div>
            </>
          )}
        </div>

        {/* Delete Error Message */}
        {deleteError && (
          <div className="mx-4 mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-700">
              <span className="font-medium">{t('posts:detail.error')}:</span> {deleteError}
            </p>
          </div>
        )}
      </div>

      {/* Platform Delete Confirmation Dialog */}
      {showDeleteConfirm && deleteConfirmData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg w-full max-w-md p-6 shadow-xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                {t('posts:detail.deletePublishedTitle')}
              </h3>
            </div>

            <p className="text-sm text-gray-600 mb-4">
              {t('posts:detail.deletePublishedDescription')}
            </p>

            <div className="space-y-2 mb-6">
              {deleteConfirmData.platforms?.map((platform) => {
                const publishedId = deleteConfirmData.publishedIds?.[platform];
                return (
                  <div
                    key={platform}
                    className={`flex items-center justify-between p-3 rounded-lg ${
                      platform === 'facebook' ? 'bg-blue-50' : 'bg-pink-50'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      {platform === 'facebook' ? (
                        <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5 text-pink-600" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069z" />
                        </svg>
                      )}
                      <span className={`font-medium ${platform === 'facebook' ? 'text-blue-900' : 'text-pink-900'}`}>
                        {platform === 'facebook' ? 'Facebook' : 'Instagram'}
                      </span>
                    </div>
                    {publishedId && (
                      <span className="text-xs text-gray-500 font-mono">
                        ID: {publishedId.substring(0, 15)}...
                      </span>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeleteConfirmData(null);
                }}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                disabled={isSubmitting}
              >
                {t('common:cancel')}
              </button>
              <button
                onClick={handleConfirmPlatformDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
                disabled={isSubmitting}
              >
                {isSubmitting ? t('posts:detail.deleting') : t('posts:detail.deleteEverywhere')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Inline Hashtag Analysis Section for Post Editor
function HashtagAnalysisSection({ text }: { text: string }) {
  const { t } = useTranslation(['posts', 'common']);
  const { status, analysisResults, isLoading, analyzeHashtags, fetchStatus } = useHashtagsStore();
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [hasAnalyzed, setHasAnalyzed] = useState(false);

  // Extract hashtags from text
  const hashtagsInText = useMemo(() => {
    const matches = text.match(/#[\w\u00C0-\u024F]+/g) || [];
    return [...new Set(matches.map(h => h.replace(/^#/, '').toLowerCase()))];
  }, [text]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handleAnalyze = async () => {
    if (hashtagsInText.length === 0) return;
    setHasAnalyzed(true);
    setShowAnalysis(true);
    await analyzeHashtags(hashtagsInText);
  };

  // Get analyzed results for hashtags in text
  const analyzedHashtags = useMemo(() => {
    return hashtagsInText.map(tag => {
      const result = analysisResults.find(r => r.hashtag.toLowerCase() === tag);
      return { tag, metrics: result };
    });
  }, [hashtagsInText, analysisResults]);

  const getEngagementColor = (engagement: number): string => {
    if (engagement > 1000) return 'text-green-600';
    if (engagement > 500) return 'text-blue-600';
    if (engagement > 100) return 'text-yellow-600';
    return 'text-gray-500';
  };

  const formatNumber = (num: number): string => {
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  // Not configured or no hashtags
  if (!status?.configured || hashtagsInText.length === 0) {
    return null;
  }

  return (
    <div className="mb-4 p-3 bg-pink-50 border border-pink-200 rounded-lg">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <svg className="w-4 h-4 text-pink-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
          </svg>
          <span className="text-sm font-medium text-pink-900">
            {t('posts:hashtagAnalysis.detected', { count: hashtagsInText.length })}
          </span>
        </div>
        <button
          onClick={handleAnalyze}
          disabled={isLoading}
          className="text-sm text-pink-700 hover:text-pink-800 font-medium flex items-center gap-1"
        >
          {isLoading ? (
            <>
              <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              {t('posts:hashtagAnalysis.analyzing')}
            </>
          ) : hasAnalyzed ? (
            t('posts:hashtagAnalysis.analyzeAgain')
          ) : (
            <>
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              {t('posts:hashtagAnalysis.checkEngagement')}
            </>
          )}
        </button>
      </div>

      {/* Hashtag List */}
      <div className="flex flex-wrap gap-1.5">
        {analyzedHashtags.map(({ tag, metrics }) => (
          <span
            key={tag}
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${
              metrics ? 'bg-white border border-pink-200' : 'bg-pink-100 text-pink-700'
            }`}
            title={metrics ? `${t('posts:hashtagAnalysis.avgEngagement')}: ${metrics.avgEngagement}` : t('posts:hashtagAnalysis.notAnalyzed')}
          >
            #{tag}
            {metrics && (
              <span className={`font-semibold ${getEngagementColor(metrics.avgEngagement)}`}>
                {formatNumber(metrics.avgEngagement)}
              </span>
            )}
          </span>
        ))}
      </div>

      {/* Expanded Analysis */}
      {showAnalysis && analyzedHashtags.some(h => h.metrics) && (
        <div className="mt-3 pt-3 border-t border-pink-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-pink-800">{t('posts:hashtagAnalysis.engagementAnalysis')}</span>
            <button
              onClick={() => setShowAnalysis(false)}
              className="text-xs text-pink-600 hover:text-pink-800"
            >
              {t('posts:hashtagAnalysis.hide')}
            </button>
          </div>
          <div className="space-y-1.5">
            {analyzedHashtags
              .filter(h => h.metrics)
              .sort((a, b) => (b.metrics?.avgEngagement || 0) - (a.metrics?.avgEngagement || 0))
              .map(({ tag, metrics }) => (
                <div key={tag} className="flex items-center justify-between text-xs bg-white p-2 rounded">
                  <span className="font-medium text-gray-700">#{tag}</span>
                  <div className="flex items-center gap-3">
                    <span className="text-gray-500">
                      <span className="font-medium text-gray-700">{formatNumber(metrics!.avgLikes)}</span> {t('posts:hashtagAnalysis.likes')}
                    </span>
                    <span className="text-gray-500">
                      <span className="font-medium text-gray-700">{formatNumber(metrics!.avgComments)}</span> {t('posts:hashtagAnalysis.comments')}
                    </span>
                    <span className={`font-semibold ${getEngagementColor(metrics!.avgEngagement)}`}>
                      {metrics!.avgEngagement > 1000 ? t('posts:hashtagAnalysis.high') :
                       metrics!.avgEngagement > 500 ? t('posts:hashtagAnalysis.medium') :
                       metrics!.avgEngagement > 100 ? t('posts:hashtagAnalysis.low') : t('posts:hashtagAnalysis.veryLow')}
                    </span>
                  </div>
                </div>
              ))}
          </div>
          {analyzedHashtags.some(h => !h.metrics) && (
            <p className="text-xs text-pink-600 mt-2">
              {t('posts:hashtagAnalysis.notAnalyzedCount', { count: analyzedHashtags.filter(h => !h.metrics).length })}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
